import React, { useState, useEffect } from "react";
import BarcodeScanner from "./BarcodeScanner";
import { Search, Clock, User, Power, RefreshCw, X, Camera, Headset, BookOpen, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import POSSupportModal from "./POSSupportModal";
import { supportAPI } from "../../api/apiService";

export default function POSHeader({
    selectedFilters = ["all", "cat_all"],
    onToggleFilter,
    activeSubCategory,
    onSubCategoryChange,
    searchQuery,
    onSearchChange,
    categories = [],
    fullCategories = [],
    onCloseOrder,
    salesmanId,
    setSalesmanId,
    onLogout,
    userRole,
    parentPhone,
    isSalesman,
    onOpenCatalogue,
    onOpenMenu
}) {
    const navigate = useNavigate();
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [activeUnreadCount, setActiveUnreadCount] = useState(0);

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            if (userRole === 'OWNER') {
                navigate('/owner/dashboard');
            } else if (userRole === 'SALES_EXECUTIVE') {
                navigate('/sales-executive/dashboard');
            } else {
                navigate('/dashboard');
            }
        }
    };

    // Identify subcategories for any of the selected main categories
    const subCategories = React.useMemo(() => {
        const subCats = [];
        const selectedCatIds = fullCategories
            .filter(c => selectedFilters.includes(String(c.id)))
            .map(c => c.id);
            
        selectedCatIds.forEach(id => {
            const cat = fullCategories.find(c => c.id === id);
            if (cat?.subcategories) {
                subCats.push(...cat.subcategories);
            }
        });
        return subCats;
    }, [fullCategories, selectedFilters]);

    // ... notification polling ...

    return (
        <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100/50 px-8 py-5 flex flex-col gap-4 shadow-sm">
            {/* Top Row: Search & Context */}
            {/* ... lines 60-130 ... */}
            <div className="flex items-center justify-between gap-8">
                {/* Back Button */}
                <button
                    onClick={handleBack}
                    className="flex items-center gap-2 px-5 py-3.5 h-14 bg-white hover:bg-gray-50 active:scale-95 text-gray-700 font-black rounded-[20px] transition-all border-2 border-gray-100 hover:border-gray-200 shadow-sm cursor-pointer shrink-0"
                    title="Go Back"
                >
                    <ArrowLeft className="w-5 h-5 text-gray-500 hover:text-gray-700" />
                    <span className="text-sm tracking-wider uppercase hidden sm:inline">Back</span>
                </button>
                {/* Search */}
                <div className="flex-1 max-w-2xl relative">
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                            <Search className="w-5 h-5 text-gray-400 group-focus-within:text-green-500 transition-colors" />
                        </div>
                        <input
                            type="text"
                            placeholder="Find products by name, barcode or HSN..."
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="w-full pl-14 pr-32 py-4 h-14 bg-green-50/10 border-2 border-transparent rounded-[20px] text-base outline-none focus:bg-white focus:border-green-500/20 focus:ring-4 focus:ring-green-500/5 transition-all font-bold placeholder:text-gray-400/80 shadow-inner"
                        />
                        <div className="absolute inset-y-0 right-2 flex items-center gap-1.5 px-2">
                            {searchQuery && (
                                <button onClick={() => onSearchChange("")} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors">
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Staff & Support */}
                <div className="flex items-center gap-4 shrink-0">
                    <div className="flex items-center gap-4 p-1.5 bg-gray-50/50 rounded-2xl border border-gray-100/50">
                        <button onClick={() => setShowSupportModal(true)} className="relative px-5 py-3 bg-white text-green-600 rounded-xl hover:bg-green-50 hover:shadow-sm transition-all shadow-xs border border-transparent hover:border-green-100 flex items-center gap-3" title="Support">
                            <Headset className="w-6 h-6 saturate-150 text-green-600" />
                            <span className="text-sm font-black tracking-wider uppercase">SUPPORT</span>
                            {activeUnreadCount > 0 && (
                                <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full text-white text-[10px] font-black flex items-center justify-center animate-bounce border-2 border-white shadow-md">
                                    {activeUnreadCount}
                                </span>
                            )}
                        </button>

                        {isSalesman && onOpenCatalogue && (
                            <button onClick={onOpenCatalogue} className="relative px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:opacity-90 hover:shadow-md transition-all border border-transparent flex items-center gap-3" title="Catalogue">
                                <BookOpen className="w-6 h-6 text-white" />
                                <span className="text-sm font-black tracking-wider uppercase">CATALOGUE</span>
                            </button>
                        )}
                        
                        <button 
                            onClick={() => {
                                if (userRole === 'OWNER') {
                                    window.location.href = '/owner/dashboard';
                                } else if (userRole === 'SALES_EXECUTIVE') {
                                    window.location.href = '/sales-executive/dashboard';
                                } else {
                                    window.location.href = '/dashboard';
                                }
                            }}
                            className="relative px-5 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 hover:shadow-lg transition-all shadow-md flex items-center gap-3"
                            title="Go to Dashboard"
                        >
                            <span className="text-sm font-black tracking-wider uppercase">DASHBOARD</span>
                        </button>
                        
                        {isSalesman && parentPhone && (
                            <a 
                                href={`https://wa.me/${String(parentPhone).replace(/\D/g, '').length === 10 ? '91' : ''}${String(parentPhone).replace(/\D/g, '')}?text=Hello%2C%20we%20need%20a%20bulk%20stock%20order.%20Kindly%20share%20the%20next%20steps.`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="relative px-5 py-3 bg-[#25D366] text-white rounded-xl hover:bg-[#20bd5a] hover:shadow-lg transition-all shadow-md border border-transparent flex items-center gap-3"
                                title={userRole === 'SALES_EXECUTIVE' || isSalesman ? "Contact Manager via WhatsApp" : "Contact Support via WhatsApp"}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                                </svg>
                                <span className="text-lg font-black tracking-wider uppercase">{parentPhone}</span>
                            </a>
                        )}
                    </div>

                    <div className="flex items-center gap-4 pl-4 pr-1.5 py-1.5 bg-green-50/50 border border-green-100/50 rounded-[20px] shadow-sm hover:bg-white hover:border-green-200 transition-all group">
                        <div className="flex flex-col items-end">
                            <span className="text-[9px] font-black text-green-500 uppercase tracking-[0.1em] leading-none mb-1">Session Active</span>
                            <span className="text-sm font-black text-gray-900 leading-tight tracking-tight">{salesmanId || "Unknown Staff"}</span>
                        </div>
                        <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-100 overflow-hidden relative">
                            <div className="w-full h-full bg-gradient-to-tr from-green-500 to-emerald-400 relative z-10 flex items-center justify-center font-black text-sm text-white">
                                {salesmanId?.charAt(0) || "S"}
                            </div>
                        </div>
                        <div className="w-px h-6 bg-green-200/50" />
                        {onLogout && (
                            <button onClick={onLogout} className="w-9 h-9 flex items-center justify-center text-green-300 hover:text-green-500 hover:bg-green-100/50 rounded-xl transition-all">
                                <Power className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Main Categories Row */}
            <div className="flex items-center gap-4">
                <nav className="flex-1 flex items-center gap-2 overflow-x-auto no-scrollbar py-2 mask-fade-right transition-all">
                    <button
                        onClick={() => onToggleFilter("all")}
                        role="checkbox"
                        aria-checked={selectedFilters.includes("all")}
                        aria-label="Filter by All Products"
                        className={`px-5 py-2 rounded-full border-2 font-black text-[10px] uppercase tracking-wider transition-all duration-200 ease-in-out whitespace-nowrap focus:ring-2 focus:ring-green-500/50 outline-none ${
                            selectedFilters.includes("all")
                                ? "border-green-500 bg-green-500 text-white shadow-md"
                                : "border-gray-100 bg-white text-gray-500 hover:bg-green-50/50"
                        }`}
                    >
                        All
                    </button>

                    <button
                        onClick={() => onToggleFilter("popular")}
                        role="checkbox"
                        aria-checked={selectedFilters.includes("popular")}
                        aria-label="Filter by Most Popular"
                        className={`px-5 py-2 rounded-full border-2 font-black text-[10px] uppercase tracking-wider transition-all duration-200 ease-in-out whitespace-nowrap focus:ring-2 focus:ring-green-500/50 outline-none ${
                            selectedFilters.includes("popular")
                                ? "border-green-500 bg-green-500 text-white shadow-md"
                                : "border-gray-100 bg-white text-gray-500 hover:bg-green-50/50"
                        }`}
                    >
                        Most Popular
                    </button>

                    <button
                        onClick={() => onToggleFilter("recommended")}
                        role="checkbox"
                        aria-checked={selectedFilters.includes("recommended")}
                        aria-label="Filter by Recommended"
                        className={`px-5 py-2 rounded-full border-2 font-black text-[10px] uppercase tracking-wider transition-all duration-200 ease-in-out whitespace-nowrap focus:ring-2 focus:ring-green-500/50 outline-none ${
                            selectedFilters.includes("recommended")
                                ? "border-green-500 bg-green-500 text-white shadow-md"
                                : "border-gray-100 bg-white text-gray-500 hover:bg-green-50/50"
                        }`}
                    >
                        Recommended
                    </button>

                    <button
                        onClick={() => onToggleFilter("special")}
                        role="checkbox"
                        aria-checked={selectedFilters.includes("special")}
                        aria-label="Filter by Special Offers"
                        className={`px-5 py-2 rounded-full border-2 font-black text-[10px] uppercase tracking-wider transition-all duration-200 ease-in-out whitespace-nowrap focus:ring-2 focus:ring-green-500/50 outline-none ${
                            selectedFilters.includes("special")
                                ? "border-green-500 bg-green-500 text-white shadow-md"
                                : "border-gray-100 bg-white text-gray-500 hover:bg-green-50/50"
                        }`}
                    >
                        Special Offers
                    </button>

                    <div className="w-px h-6 bg-gray-200 mx-2" />

                    <button
                        onClick={() => onToggleFilter("cat_all")}
                        role="checkbox"
                        aria-checked={selectedFilters.includes("cat_all")}
                        aria-label="Filter by All Categories"
                        className={`px-5 py-2 rounded-full border-2 font-black text-[10px] uppercase tracking-wider transition-all duration-200 ease-in-out whitespace-nowrap focus:ring-2 focus:ring-green-500/50 outline-none ${
                            selectedFilters.includes("cat_all")
                                ? "border-green-500 bg-green-500 text-white shadow-md"
                                : "border-gray-100 bg-white text-gray-500 hover:bg-green-50/50"
                        }`}
                    >
                        All Categories
                    </button>

                    {categories.map((cat) => {
                        const catKey = String(cat.id);
                        const isSelected = selectedFilters.includes(catKey);
                        return (
                            <button
                                key={cat.id}
                                onClick={() => onToggleFilter(catKey)}
                                role="checkbox"
                                aria-checked={isSelected}
                                aria-label={`Filter by ${cat.name}`}
                                className={`px-5 py-2 rounded-full border-2 font-black text-[10px] uppercase tracking-wider transition-all duration-200 ease-in-out whitespace-nowrap focus:ring-2 focus:ring-green-500/50 outline-none ${
                                    isSelected
                                        ? "border-green-500 bg-green-500 text-white shadow-md"
                                        : "border-gray-100 bg-white text-gray-500 hover:bg-green-50/50"
                                }`}
                            >
                                {cat.name}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Sub-Categories Row (Conditionally Rendered) */}
            {subCategories.length > 0 && (
                <div className="flex items-center gap-4 pt-3 border-t border-gray-100/30 animate-in fade-in slide-in-from-top-2 duration-300">
                    <nav className="flex-1 flex gap-2.5 overflow-x-auto premium-scrollbar pb-3">
                        <button
                            onClick={() => onSubCategoryChange(null)}
                            className={`px-5 py-2.5 rounded-2xl border-2 font-black text-[10px] uppercase tracking-[0.1em] transition-all whitespace-nowrap ${!activeSubCategory
                                ? "bg-green-500 text-white border-green-500 shadow-lg shadow-green-500/20"
                                : "bg-gray-50/50 text-gray-400 border-transparent hover:bg-gray-100 hover:text-gray-600"
                                }`}
                        >
                            All Items
                        </button>
                        {subCategories.map((sub) => (
                            <button
                                key={sub.id}
                                onClick={() => onSubCategoryChange(sub.id)}
                                className={`px-5 py-2.5 rounded-2xl border-2 font-black text-[10px] uppercase tracking-[0.1em] transition-all whitespace-nowrap ${parseInt(activeSubCategory) === sub.id
                                    ? "bg-green-500 text-white border-green-500 shadow-lg shadow-green-500/20"
                                    : "bg-gray-50/50 text-gray-400 border-transparent hover:bg-gray-100 hover:text-gray-600"
                                    }`}
                            >
                                {sub.name}
                            </button>
                        ))}
                    </nav>
                </div>
            )}

            <POSSupportModal
                isOpen={showSupportModal}
                onClose={() => setShowSupportModal(false)}
            />
        </div>
    );
}
